// The two pages a signed link lands on: /confirm and /unsubscribe.
//
// Both are PUBLIC — no session required — because that is the entire point. A
// day-of "still coming?" text that lands on a login screen is worthless, and an
// unsubscribe link that demands a password is hostile.
//
// The confirm page acts IMMEDIATELY on load rather than showing a button. The
// member already expressed intent by tapping the link in their text message;
// making them tap again is friction with no purpose. The unsubscribe page does the
// same, and offers an undo, because the common mistake there is an accidental tap
// rather than a change of heart.

import {
  ApiClientError,
  confirmByToken,
  resubscribeByToken,
  unsubscribeByToken,
} from '../api/client.js';
import { type View } from '../router.js';
import { el } from '../dom.js';
import { fmtInstant, TimeFormat } from '@msc/shared';
import { viewerZoneSync } from '../viewerZone.js';

/** /confirm?t=… — tap-to-confirm a day-of RSVP. */
export const confirmTokenView: View = async (outlet, ctx) => {
  const token = ctx.query.get('t') ?? '';
  const main = el('main', { class: 'auth-card' }, [
    el('h1', {}, 'Confirming'),
    el('p', { class: 'muted' }, 'One moment'),
  ]);
  outlet.append(main);

  if (!token) {
    main.replaceChildren(
      el('h1', {}, 'Link not valid'),
      el('p', { class: 'form-error' }, 'That link is missing its code. Open the event instead.'),
      el('p', {}, [el('a', { href: '/' }, 'Go to MySocialCal')]),
    );
    return;
  }

  try {
    const result = await confirmByToken(token);
    // The zone here is the browser's, not the account's: the visitor may not be
    // logged in, so there is no saved zone to read.
    const when = result.confirmedDate
      ? fmtInstant(result.confirmedDate, viewerZoneSync(), TimeFormat.INSTANT)
      : '';

    main.replaceChildren(
      el('h1', {}, result.alreadyConfirmed ? 'Already confirmed' : 'You’re in'),
      el(
        'p',
        {},
        result.alreadyConfirmed
          ? `You had already confirmed for ${result.eventTitle}.`
          : `Thanks, you’re confirmed for ${result.eventTitle}.`,
      ),
      ...(when ? [el('p', { class: 'muted small' }, `Confirmed ${when}`)] : []),
      el('p', {}, [el('a', { href: `/events/${result.eventId}` }, 'Open the event')]),
    );
  } catch (err) {
    const code = err instanceof ApiClientError ? err.code : null;
    // Each failure gets its own sentence, and each says what to do next. A dead
    // end on a page reached from a text message is where people give up.
    const message =
      code === 'RSVP_NOT_YES'
        ? 'You are not marked as going to this event yet. Open the event to RSVP.'
        : code === 'EVENT_ENDED'
          ? 'That event has already ended.'
          : code === 'GONE'
            ? 'That link has expired. Open the event to confirm.'
            : err instanceof ApiClientError
              ? err.message
              : 'Something went wrong. Open the event to confirm.';

    main.replaceChildren(
      el('h1', {}, 'Could not confirm'),
      el('p', { class: 'form-error' }, message),
      el('p', {}, [el('a', { href: '/' }, 'Go to MySocialCal')]),
    );
  }
};

/** /unsubscribe?t=… — turn off notification email for one address. */
export const unsubscribeView: View = async (outlet, ctx) => {
  const token = ctx.query.get('t') ?? '';
  const main = el('main', { class: 'auth-card' }, [
    el('h1', {}, 'Unsubscribing'),
    el('p', { class: 'muted' }, 'One moment'),
  ]);
  outlet.append(main);

  if (!token) {
    main.replaceChildren(
      el('h1', {}, 'Link not valid'),
      el('p', { class: 'form-error' }, 'That link is missing its code.'),
    );
    return;
  }

  try {
    const result = await unsubscribeByToken(token);
    main.replaceChildren(
      el('h1', {}, 'Unsubscribed'),
      el('p', {}, `${result.email} will no longer receive notification email.`),
      el(
        'p',
        { class: 'muted small' },
        'Account email (password resets and address verification) still sends, because ' +
          'that is how you get back into your account.',
      ),
      undoSection(token),
    );
  } catch (err) {
    main.replaceChildren(
      el('h1', {}, 'Could not unsubscribe'),
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.code === 'GONE'
          ? 'That link has expired. You can turn notifications off in your settings instead.'
          : err instanceof ApiClientError
            ? err.message
            : 'Something went wrong.',
      ),
      el('p', {}, [el('a', { href: '/settings/notifications' }, 'Open notification settings')]),
    );
  }
};

/**
 * An undo, right there on the page.
 *
 * The realistic failure mode for an unsubscribe link is a mis-tap on a phone, and
 * without this the only recovery is logging in — which the person may not be able
 * to do from the device they are holding.
 */
function undoSection(token: string): HTMLElement {
  const status = el('p', { class: 'muted small' });
  const btn = el('button', { class: 'btn btn-link btn-standalone', type: 'button' }, 'Undo: resubscribe me');

  btn.addEventListener('click', async () => {
    btn.setAttribute('disabled', 'disabled');
    try {
      const result = await resubscribeByToken(token);
      status.textContent = `${result.email} will receive notification email again.`;
      btn.remove();
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not resubscribe you.';
      btn.removeAttribute('disabled');
    }
  });

  return el('section', {}, [btn, status]);
}
